import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from 'components/core/Grid';
import Input from 'components/core/Input';
import Button from 'components/core/Button';
import Checkbox from 'components/core/Checkbox';
import LookupTableInput from 'components/LookupTableInput';
import Typography from 'components/core/Typography';
import NoteAdd from 'components/NoteAdd';

import lookupTableActions from 'actions/lookupTableActions';
import locationActions from 'actions/locationActions';
import { Profile } from 'domain';

const initialState = {
    label: '',
    parent: null,
    coordinates: {
        latitude: '',
        longitude: ''
    },
    events: [],
    isCentroid: false
};

const LocationForm = ({ onSubmit, initialValues, notes = [] }) => {
    const dispatch = useDispatch();
    const [location, setLocation] = useState(initialValues ? {
        label: initialValues?.get?.('label') || initialValues?.label || '',
        parent: initialValues?.get?.('parent') || initialValues?.parent || null,
        coordinates: {
            latitude: initialValues?.get?.('coordinates')?.latitude || initialValues?.coordinates?.latitude || '',
            longitude: initialValues?.get?.('coordinates')?.longitude || initialValues?.coordinates?.longitude || ''
        },
        isCentroid: initialValues?.get?.('isCentroid') || initialValues?.isCentroid || false,
        events: initialValues?.get?.('events') || initialValues?.events || [],
    } : initialState);
    const [errors, setErrors] = useState({});
    const [formNotes, setFormNotes] = useState(notes);
    const [originalNotes, setOriginalNotes] = useState(notes);
    const options = useSelector(state => state.lookupTable.options);
    const me = useSelector(({ profile }) => profile.me);
    const isAdmin = me?.type === Profile.TYPE_ADMIN;
    
    useEffect(() => {
        setFormNotes(notes);
        setOriginalNotes(notes);
    }, [notes]);

    const handleNoteChange = (index, field, value) => {
        const updatedNotes = [...formNotes];
        updatedNotes[index] = { ...updatedNotes[index], [field]: value };
        setFormNotes(updatedNotes);
    };

    const handleAddNote = () => {
        setFormNotes([...formNotes, { type: null, text: '', isDefault: false }]);
    };

    const handleRemoveNote = (index) => {
        const updatedNotes = formNotes.filter((_, i) => i !== index);
        setFormNotes(updatedNotes);
    };

    const getNoteDiff = (original, updated) => {
        const toCreate = updated.filter(note => !note.id && note.type && note.text.trim());
        const toUpdate = updated.filter(note => {
            if (!note.id || !note.text.trim()) return false;
            const originalNote = original.find(orig => orig.id === note.id);
            if (!originalNote) return false;
            return (
                originalNote.type?.id !== note.type?.id || 
                originalNote.text !== note.text
            );
        });
        const toDelete = original.filter(orig => 
            orig.id && !updated.find(upd => upd.id === orig.id)
        );
        return { toCreate, toUpdate, toDelete };
    };
    
    const handleSave = async () => {
        setErrors({});
        
        const hasLatitude = location.coordinates.latitude !== '' && location.coordinates.latitude != null;
        const hasLongitude = location.coordinates.longitude !== '' && location.coordinates.longitude != null;
        
        if (!isAdmin) {
            if (!hasLatitude) {
                setErrors(prev => ({ ...prev, 'coordinates.latitude': 'Latitude is required' }));
                return;
            }
            if (!hasLongitude) {
                setErrors(prev => ({ ...prev, 'coordinates.longitude': 'Longitude is required' }));
                return;
            }
        }
        
        if (hasLatitude) {
            const lat = parseFloat(location.coordinates.latitude);
            if (isNaN(lat) || lat < -90 || lat > 90) {
                setErrors(prev => ({ ...prev, 'coordinates.latitude': 'Invalid latitude (-90 to 90)' }));
                return;
            }
        }
        
        if (hasLongitude) {
            const lng = parseFloat(location.coordinates.longitude);
            if (isNaN(lng) || lng < -180 || lng > 180) {
                setErrors(prev => ({ ...prev, 'coordinates.longitude': 'Invalid longitude (-180 to 180)' }));
                return;
            }
        }
        
        if (!isAdmin && hasLatitude !== hasLongitude) {
            setErrors(prev => ({ 
                ...prev, 
                'coordinates.latitude': !hasLatitude ? 'Both coordinates required' : prev['coordinates.latitude'],
                'coordinates.longitude': !hasLongitude ? 'Both coordinates required' : prev['coordinates.longitude']
            }));
            return;
        }

        if (!location.label) {
            setErrors(prev => ({ ...prev, label: 'Label is required.' }));
            return;
        }
    
        try {
            const locationData = {
                ...location,
                coordinates: (hasLatitude && hasLongitude) ? { 
                    latitude: parseFloat(location.coordinates.latitude), 
                    longitude: parseFloat(location.coordinates.longitude) 
                } : null,
                events: location.events?.map(event => event?.id || event) || []
            };
            
            if (initialValues?.id || initialValues?.get?.('id')) {
                locationData.objectId = initialValues.id || initialValues.get('id');
            }
            
            const savedLocation = await dispatch(locationActions.save(locationData));
            await dispatch(lookupTableActions.fetchAllOptions());
            
            const noteDiff = getNoteDiff(originalNotes, formNotes);
            onSubmit(savedLocation.value, formNotes, noteDiff);
        } catch (err) {
            setErrors(err.inner?.reduce((acc, curr) => {
                acc[curr.path] = curr.message;
                return acc;
            }, {}) || { general: 'Failed to save location' });
        }
    };

    const handleCoordinateChange = (field, value) => {
        setLocation(prev => ({
            ...prev,
            coordinates: {
                ...prev.coordinates,
                [field]: value
            }
        }));
    };

    const handleFieldChange = (field, value) => {
        setLocation(prev => ({
            ...prev,
            [field]: value
        }));
    }

    return (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <Input
                    label="Label"
                    value={location.label}
                    onChange={(value) => handleFieldChange('label', value)}
                    error={errors.label}
                    required
                />
            </Grid>

            <Grid item xs={12}>
                <LookupTableInput
                    id="parent"
                    label="Parent Location"
                    value={location.parent}
                    onChange={(value) => handleFieldChange('parent', value)}
                    options={options.locations}
                    getOptionLabel={(option) => option?.get('label')}
                    error={errors.location}
                    suppressForm={true}
                />
            </Grid>

            <Grid item xs={12}>
                <LookupTableInput
                    label="Events"
                    value={location.events}
                    onChange={(value) => handleFieldChange('events', value)}
                    options={options.events}
                    getOptionLabel={(option) => option?.get('name')}
                    error={errors.events}
                    multiple
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Input
                    label="Latitude"
                    type="number"
                    value={location.coordinates.latitude}
                    onChange={(value) => handleCoordinateChange('latitude', value)}
                    error={errors['coordinates.latitude']}
                    required={!isAdmin}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <Input
                    label="Longitude"
                    type="number"
                    value={location.coordinates.longitude}
                    onChange={(value) => handleCoordinateChange('longitude', value)}
                    error={errors['coordinates.longitude']}
                    required={!isAdmin}
                />
            </Grid>

            <Grid item xs={12}>
                <Checkbox
                    label="Is Centroid"
                    checked={location.isCentroid}
                    onChange={(value) => handleFieldChange('isCentroid', value)}
                />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Notes</Typography>
            </Grid>

            <Grid item xs={12}>
                <NoteAdd
                    parentForm="Location"
                    notes={formNotes}
                    options={options}
                    onNoteChange={handleNoteChange}
                    onAddNote={handleAddNote}
                    onRemoveNote={handleRemoveNote}
                />
            </Grid>

            <Grid item xs={12}>
                <Button onClick={handleSave}>
                    {initialValues ? 'Update' : 'Create'} Location
                </Button>
            </Grid>
        </Grid>
    );
};

export default LocationForm;
